import React, { useRef, useCallback } from 'react';
import styled, { css } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { CHOICE_RESULT_KEYS, ITEM_TEXT_DELIMITER_TYPE } from '@Services/Editor/Constants';

import themes, { THEME_TYPES } from '@Styles/Themes';

import useContextMenu from '@Hooks/useContextMenu';
import useOffsetSize from '@Hooks/useOffsetSize';
import {
  addEmptyChoiceResultStoreProperty,
  addEmptyChoiceResultDestroyStoreProperty,
  addEmptyResultProbabilityCondition,
  addEmptyResultRequiredItemsCondition,
  updateResultPropertyChipState,
} from '@Services/Editor';

import { ReactComponent as AddPropertiesSVG } from '@Assets/Svgs/ic_properties_add.svg';
import { ReactComponent as DestroyPropertiesSVG } from '@Assets/Svgs/ic_properties_delete.svg';
import { ReactComponent as StatesPropertiesSVG } from '@Assets/Svgs/ic_properties_states.svg';
import { ReactComponent as AchievementPropertiesSVG } from '@Assets/Svgs/ic_properties_achievement.svg';
import Button from '@Components/Common/Button';
import TX from '@Components/Common/Text';
import AddingPropertyContextMenu from './AddingPropertyContextMenu';

const AddingPropertyButton = ({ processId, choiceId, resultId, isVisible }) => {
  const dispatch = useDispatch();
  // const result = useSelector(({ editor }) => editor.processes[processId].choices[choiceId].results[resultId]);

  const processesResults = useSelector(({ editor }) => editor.processes[processId].choices[choiceId].results);

  const result = processesResults.find(v => v.resultId === resultId);

  const addingPropertyButtonRef = useRef(null);
  const { isContextMenuVisible, handleShowContextMenuClick } = useContextMenu();
  const { left, top } = useOffsetSize(addingPropertyButtonRef, isContextMenuVisible);

  const theme = themes[THEME_TYPES.LIGHT];

  const handleAddStoreItemClick = useCallback(() => {
    dispatch(
      addEmptyChoiceResultStoreProperty({
        processId,
        choiceId,
        resultId,
      }),
    );
  }, [dispatch]);

  const handleDestroyStoreItemClick = useCallback(() => {
    dispatch(
      addEmptyChoiceResultDestroyStoreProperty({
        processId,
        choiceId,
        resultId,
      }),
    );
  }, [dispatch]);

  const handleChangeStatusItemClick = useCallback(() => {
    dispatch(
      updateResultPropertyChipState({
        processId,
        choiceId,
        resultId,
        type: CHOICE_RESULT_KEYS.CHANGE_STATUS,
        state: true,
      }),
    );
  }, [dispatch]);

  const handleAchievementVisibleItemClick = useCallback(() => {
    dispatch(
      updateResultPropertyChipState({
        processId,
        choiceId,
        resultId,
        type: CHOICE_RESULT_KEYS.ACHIEVEMENT,
        state: true,
      }),
    );
  }, [dispatch]);

  const handleProbabilityConditionItemClick = useCallback(() => {
    dispatch(addEmptyResultProbabilityCondition({ processId, choiceId, resultId }));
  }, [dispatch]);

  const handleRequiredItemsClick = useCallback(() => {
    dispatch(addEmptyResultRequiredItemsCondition({ processId, choiceId, resultId }));
  }, [dispatch]);

  const CONTEXT_MENU_ITEM = [
    {
      types: `${CHOICE_RESULT_KEYS.STORE}, ${CHOICE_RESULT_KEYS.STORE_HIDDEN}`,
      title: '얻는 아이템',
      Icon: <AddIcon fill="#28C848" />,
      onClick: () => handleAddStoreItemClick(),
    },
    {
      types: `${CHOICE_RESULT_KEYS.DESTROY}, ${CHOICE_RESULT_KEYS.DESTROY_HIDDEN}`,
      title: '잃는 아이템',
      Icon: <DestroyIcon />,
      onClick: () => handleDestroyStoreItemClick(),
    },
    {
      type: CHOICE_RESULT_KEYS.CHANGE_STATUS,
      title: '상태 변화',
      Icon: <StatesIcon />,
      onClick: () => handleChangeStatusItemClick(),
    },
    {
      type: CHOICE_RESULT_KEYS.ACHIEVEMENT,
      title: '업적 달성',
      Icon: <AchievementIcon />,
      onClick: () => handleAchievementVisibleItemClick(),
    },
    {
      type: CHOICE_RESULT_KEYS.PROBABILITY,
      title: '확률',
      bgText: '확률',
      onClick: () => handleProbabilityConditionItemClick(),
    },
    {
      type: CHOICE_RESULT_KEYS.REQUIRED_ITEMS,
      title: '아이템 보유 조건',
      bgText: '보유',
      onClick: () => handleRequiredItemsClick(),
    },
  ];

  const getFilteredContextMenuItemList = () => {
    let contextMenuItemList = CONTEXT_MENU_ITEM.filter(({ Icon, types, type }) => {
      let isHiddenItem = false;

      if (types) {
        const splittedTypes = Icon && types.split(ITEM_TEXT_DELIMITER_TYPE.SPLIT);
        isHiddenItem = splittedTypes && splittedTypes.some(splittedType => Object.keys(result).includes(splittedType));
      } else {
        const isBooleanType =
          type === CHOICE_RESULT_KEYS.CHANGE_STATUS ||
          type === CHOICE_RESULT_KEYS.ACHIEVEMENT ||
          type === CHOICE_RESULT_KEYS.PROBABILITY;
        if (isBooleanType) {
          const { resultPropertyChipState } = result;
          isHiddenItem = resultPropertyChipState[type];
        } else {
          isHiddenItem = Object.keys(result).includes(type);
        }
      }

      return !isHiddenItem;
    });
    const hasProbability = contextMenuItemList.some(item => item.type === 'probability');
    const hasRequiredItems = contextMenuItemList.some(item => item.type === 'requiredItems');

    contextMenuItemList = contextMenuItemList.filter(
      item =>
        !(item.type === 'requiredItems' && !hasProbability) && !(item.type === 'probability' && !hasRequiredItems),
    );

    return contextMenuItemList;
  };

  const contextMenuItemList = getFilteredContextMenuItemList();
  const isAddingPropertyButtonVisible = contextMenuItemList.length;

  if (!isAddingPropertyButtonVisible) {
    return null;
  }

  return (
    <ButtonContainer isFocused={isContextMenuVisible} isVisible={isVisible}>
      <Button
        buttonRef={addingPropertyButtonRef}
        isTextOnly={false}
        backgroundColor={theme.colors.ui_1}
        style={{ padding: '6px 10px 6px 6px', height: '28px' }}
        onClick={event => handleShowContextMenuClick(event)}
      >
        <AddIconWrapper>
          <AddIcon fill={theme.colors.ui_on_2} />
        </AddIconWrapper>

        <TX.FootnoteM>속성 추가</TX.FootnoteM>

        {isContextMenuVisible && (
          <AddingPropertyContextMenu
            contextMenuItemList={contextMenuItemList}
            left={left}
            top={top}
            choiceId={choiceId}
            resultId={resultId}
          />
        )}
      </Button>
    </ButtonContainer>
  );
};

AddingPropertyButton.propTypes = {
  processId: PropTypes.number.isRequired,
  choiceId: PropTypes.number.isRequired,
  resultId: PropTypes.number.isRequired,
};

const IconCSS = css`
  width: 16px;
  height: 16px;
`;

const ButtonContainer = styled.div`
  border-radius: 8px;
  border: ${({ isFocused }) => isFocused && '2px solid #8FE3EF'};
  display: ${({ isVisible }) => (isVisible ? 'inline-block' : 'none')};
`;

const StyledIconWrapper = styled.div`
  ${IconCSS}

  display: flex;
  align-items: center;
  justify-content: center;
`;

const AddIconWrapper = styled(StyledIconWrapper)`
  margin-right: 4px;
`;

const AddIcon = styled(AddPropertiesSVG)`
  ${IconCSS}
`;

const DestroyIcon = styled(DestroyPropertiesSVG)`
  ${IconCSS}
`;

const StatesIcon = styled(StatesPropertiesSVG)`
  ${IconCSS}
`;

const AchievementIcon = styled(AchievementPropertiesSVG)`
  ${IconCSS}

  fill: #FFE456;
`;
export default AddingPropertyButton;
